import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Flag, Mail, Phone, Users, AlertTriangle, Copy } from "lucide-react";

export default function AdminSyndicats() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [created, setCreated] = useState(null);

  function empty() {
    return { name: "", contact_nom: "", contact_prenom: "", email: "", telephone: "", secteur: "", description: "", password: "" };
  }

  const load = async () => {
    try { const { data } = await api.get("/admin/syndicats"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/syndicats", form);
      setCreated(data);
      setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce syndicat ? Cette action supprimera aussi tous ses adhérents, plaintes et actualités.")) return;
    try { await api.delete(`/admin/syndicats/${id}`); toast.success("Syndicat supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Syndicats"
        title="Organisations syndicales"
        sub="Créez les comptes syndicat (PAG-SYNDIC-XXXX). Ils gèrent leurs adhérents, plaintes et actualités."
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreated(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="syndicat-add-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Créer un syndicat</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              {!created ? (
                <>
                  <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><Flag className="h-5 w-5 text-[#FFD700]" /> Nouveau syndicat</DialogTitle></DialogHeader>
                  <form onSubmit={submit} className="space-y-3" data-testid="syndicat-form">
                    <F label="Nom du syndicat" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="synd-name" />
                    <div className="grid grid-cols-2 gap-3">
                      <F label="Contact - Nom" value={form.contact_nom} onChange={(v) => setForm({ ...form, contact_nom: v })} required testid="synd-nom" />
                      <F label="Contact - Prénom" value={form.contact_prenom} onChange={(v) => setForm({ ...form, contact_prenom: v })} required testid="synd-prenom" />
                    </div>
                    <F label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="synd-email" />
                    <div className="grid grid-cols-2 gap-3">
                      <F label="Téléphone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="synd-tel" />
                      <F label="Secteur" value={form.secteur} onChange={(v) => setForm({ ...form, secteur: v })} testid="synd-secteur" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Description (optionnel)</Label>
                      <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="synd-desc" />
                    </div>
                    <F label="Mot de passe initial (min. 6 caractères)" type="text" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="synd-pwd" />
                    <DialogFooter><Button type="submit" data-testid="synd-submit" className="bg-[#002FA7] text-white">Créer</Button></DialogFooter>
                  </form>
                </>
              ) : (
                <div className="space-y-4" data-testid="synd-created">
                  <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] text-sm font-semibold">Syndicat créé avec succès !</div>
                  <div className="p-4 border border-[#0A0A0A] bg-[#0A0A0A] text-white font-mono text-sm space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFD700] font-bold">Identifiants de connexion</div>
                    <div className="flex items-center gap-2"><span className="text-white/60">Code :</span><span className="font-bold text-base">{created.code}</span><Button size="sm" variant="ghost" onClick={() => copy(created.code)} className="h-6 px-1 text-white"><Copy className="h-3 w-3" /></Button></div>
                    <div className="flex items-center gap-2"><span className="text-white/60">Mot de passe :</span><span className="font-bold text-base">{created.password}</span><Button size="sm" variant="ghost" onClick={() => copy(created.password)} className="h-6 px-1 text-white"><Copy className="h-3 w-3" /></Button></div>
                  </div>
                  <Button onClick={() => { setOpen(false); setCreated(null); }} className="w-full bg-[#0A0A0A] text-white">Fermer</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="grid gap-3" data-testid="syndicats-list">
          {items.length === 0 ? (
            <div className="bg-white border border-[#DEE2E6] p-10 text-center text-sm text-[#868E96]">
              <Flag className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucun syndicat. Cliquez sur "Créer un syndicat".
            </div>
          ) : items.map((s) => (
            <div key={s.id} className="bg-white border border-[#DEE2E6] p-5 grid lg:grid-cols-12 gap-4 items-center" data-testid={`syndicat-row-${s.id}`}>
              <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-bold flex items-center gap-1.5"><Flag className="h-3 w-3" /> {s.code}</div>
                <div className="font-display font-bold mt-1">{s.name}</div>
                <div className="text-[10px] text-[#868E96] mt-0.5">{s.contact_prenom} {s.contact_nom}</div>
              </div>
              <div className="lg:col-span-3 text-xs text-[#495057] space-y-0.5">
                <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {s.email}</div>
                {s.telephone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {s.telephone}</div>}
              </div>
              <div className="lg:col-span-3 flex gap-4 text-xs">
                <div><span className="text-[#868E96]">Adhérents :</span> <b>{s.nb_adherents}</b></div>
                <div><span className="text-[#868E96]">Plaintes :</span> <b>{s.nb_plaintes}</b></div>
              </div>
              <div className="lg:col-span-2 flex justify-end">
                <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`syndicat-delete-${s.id}`} className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3 mr-1" /> Supprimer</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="h-11" />
    </div>
  );
}
